export type SafetySchema = Record<string, unknown>;
export type SafetyPrimitive = string | number | boolean;
export interface SafetyEnumOption { value: SafetyPrimitive; label: string }
export type SafetySchemaType = "object" | "array" | "string" | "number" | "integer" | "boolean";

export function safetyRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function primitive(value: unknown): value is SafetyPrimitive {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

function nodeType(schema: SafetySchema): SafetySchemaType | undefined {
  const types = Array.isArray(schema.type) ? schema.type.filter(type => type !== "null") : [schema.type];
  const type = types.length === 1 ? types[0] : undefined;
  if (["object", "array", "string", "number", "integer", "boolean"].includes(String(type))) return type as SafetySchemaType;
  if (safetyRecord(schema.properties)) return "object";
  if (safetyRecord(schema.items)) return "array";
  const values = Array.isArray(schema.enum) ? schema.enum : "const" in schema ? [schema.const] : [];
  if (values.length && values.every(primitive) && values.every(value => typeof value === typeof values[0])) {
    return typeof values[0] as "string" | "number" | "boolean";
  }
  return undefined;
}

/** Only alternatives that can be represented without choosing a structural branch are editable. */
export function inspectSafetySchema(schema: SafetySchema, root: SafetySchema = schema, depth = 0): {
  schema: SafetySchema; type?: SafetySchemaType; reason?: string;
} {
  if (depth > 24) return { schema, reason: "Die Schema-Verschachtelung ist zu tief für eine sichere Bearbeitung." };
  if (Array.isArray(schema.enum) && schema.enum.length === 0) return { schema, reason: "Amazon definiert für dieses Feld keine zulässigen Werte." };
  if (typeof schema.$ref === "string") {
    let referenced: unknown = root;
    if (!schema.$ref.startsWith("#/")) return { schema, reason: "Dieser Amazon-Schema-Verweis wurde nicht aufgelöst." };
    for (const segment of schema.$ref.slice(2).split("/")) {
      referenced = safetyRecord(referenced) ? referenced[segment.replaceAll("~1", "/").replaceAll("~0", "~")] : undefined;
    }
    if (!safetyRecord(referenced)) return { schema, reason: "Dieser Amazon-Schema-Verweis wurde nicht aufgelöst." };
    const { $ref: _ref, ...overrides } = schema;
    return inspectSafetySchema({ ...referenced, ...overrides }, root, depth + 1);
  }
  const alternatives = schema.oneOf ?? schema.anyOf;
  if (Array.isArray(alternatives) && alternatives.length) {
    if (alternatives.some(node => !safetyRecord(node))) return { schema, reason: "Die Amazon-Schema-Varianten können nicht eindeutig bearbeitet werden." };
    const nodes = alternatives.filter(safetyRecord).filter(node => node.type !== "null").map(node => inspectSafetySchema(node, root, depth + 1));
    const { anyOf: _any, oneOf: _one, ...base } = schema;
    if (nodes.length === 1 && !nodes[0].reason) return inspectSafetySchema({ ...nodes[0].schema, ...base }, root, depth + 1);
    const type = nodes[0]?.type;
    if (!nodes.length || nodes.some(node => node.reason || node.type !== type || !safetyEnumOptions(node.schema).length) || type === "object" || type === "array") {
      return { schema, reason: "Amazon definiert mehrere Schema-Varianten. Diese Struktur kann hier nicht eindeutig bearbeitet werden." };
    }
    const allOptions = nodes.flatMap(node => safetyEnumOptions(node.schema));
    if (Array.isArray(schema.oneOf) && allOptions.some((option, index) => allOptions.findIndex(other => Object.is(other.value, option.value)) !== index)) {
      return { schema, reason: "Die Amazon-Schema-Varianten überlappen sich und können nicht sicher zusammengeführt werden." };
    }
    const parentEnums = Array.isArray(base.enum) ? base.enum : undefined;
    const options = allOptions.filter((option, index, all) => all.findIndex(other => Object.is(other.value, option.value)) === index && (!parentEnums || parentEnums.some(value => Object.is(value, option.value))));
    return inspectSafetySchema({ ...base, type, enum: options.map(option => option.value), enumNames: options.map(option => option.label) }, root, depth + 1);
  }
  if (Array.isArray(schema.allOf) && schema.allOf.length) {
    // Required-only intersections are common in Amazon object schemas; conditional branches need server review.
    const simple = schema.allOf.every(node => safetyRecord(node) && Object.keys(node).every(key => key === "required"));
    if (!simple) return { schema, reason: "Amazon definiert zusätzliche bedingte Schema-Regeln. Diese Struktur ist hier nur lesbar." };
    const required = [...new Set([...(Array.isArray(schema.required) ? schema.required : []), ...schema.allOf.flatMap(node => safetyRecord(node) && Array.isArray(node.required) ? node.required : [])])];
    const { allOf: _all, ...base } = schema;
    return inspectSafetySchema({ ...base, required }, root, depth + 1);
  }
  const type = nodeType(schema);
  if (!type) return { schema, reason: "Amazon liefert für diese Struktur keinen unterstützten Eingabetyp." };
  if (Array.isArray(schema.enum) && schema.enum.some(value => !primitive(value))) return { schema, type, reason: "Die Amazon-Werteliste enthält komplexe Werte und ist hier nur lesbar." };
  if (schema.readOnly === true || schema.editable === false) return { schema, type, reason: "Dieses Feld ist laut Amazon-Schema schreibgeschützt." };
  return { schema, type };
}

export function safetyEnumOptions(schema: SafetySchema): SafetyEnumOption[] {
  const values = Array.isArray(schema.enum) ? schema.enum : "const" in schema ? [schema.const] : [];
  const labels = Array.isArray(schema.enumNames) ? schema.enumNames : [];
  return values.flatMap((value, index) => primitive(value) ? [{ value, label: typeof labels[index] === "string" ? labels[index] as string : typeof schema.title === "string" && "const" in schema ? schema.title : String(value) }] : []);
}

/** No marketplace, language or compliance answer is inferred from a code or a single-option enum. */
export function seedSafetyValue(schema: SafetySchema, root: SafetySchema = schema, depth = 0): unknown {
  if (depth > 24) return undefined;
  if ("const" in schema) return structuredClone(schema.const);
  if ("default" in schema) return structuredClone(schema.default);
  const inspected = inspectSafetySchema(schema, root, depth);
  if (inspected.reason) return undefined;
  if (inspected.type === "object" && safetyRecord(inspected.schema.properties)) {
    const result: Record<string, unknown> = {};
    for (const [key, node] of Object.entries(inspected.schema.properties)) {
      if (!safetyRecord(node)) continue;
      const value = seedSafetyValue(node, root, depth + 1);
      if (value !== undefined && !(safetyRecord(value) && !Object.keys(value).length)) result[key] = value;
    }
    return result;
  }
  if (inspected.type === "array") return [];
  return undefined;
}

export function setSafetyObjectValue(value: unknown, key: string, next: unknown, schema: SafetySchema, root: SafetySchema = schema): Record<string, unknown> {
  const seeded = seedSafetyValue(schema, root);
  const result = { ...(safetyRecord(seeded) ? seeded : {}), ...(safetyRecord(value) ? value : {}) };
  if (next === undefined) delete result[key];
  else result[key] = next;
  return result;
}

export function safetyChoiceValue(value: unknown): unknown {
  return safetyRecord(value) ? value.value : value;
}

export function toggleSafetyChoice(value: unknown, option: SafetyPrimitive, itemSchema: SafetySchema, checked: boolean, root: SafetySchema = itemSchema): unknown[] {
  const current = Array.isArray(value) ? value : [];
  if (!checked) return current.filter(item => !Object.is(safetyChoiceValue(item), option));
  if (current.some(item => Object.is(safetyChoiceValue(item), option))) return current.slice();
  const inspected = inspectSafetySchema(itemSchema, root);
  return [...current, inspected.type === "object" ? setSafetyObjectValue(undefined, "value", option, inspected.schema, root) : option];
}

export function replaceSafetyChoice(value: unknown, option: SafetyPrimitive, itemSchema: SafetySchema, root: SafetySchema = itemSchema): unknown[] {
  const current = Array.isArray(value) ? value : [];
  const inspected = inspectSafetySchema(itemSchema, root);
  const replacement = inspected.type === "object" ? setSafetyObjectValue(current[0], "value", option, inspected.schema, root) : option;
  return [replacement, ...current.slice(1)];
}

export function safetyOptionGroup(value: SafetyPrimitive, label: string): string {
  const code = safetyCanonicalCode(value) ?? leadingSafetyCode(label)?.code;
  if (code?.startsWith("EUH")) return "Ergänzende Gefahrenhinweise (EUH)";
  if (code?.startsWith("H")) return "Gefahrenhinweise (H)";
  if (code?.startsWith("P")) return "Sicherheitshinweise (P)";
  return "Amazon-Auswahl";
}

const GHS_GRAPHICS: Record<string, string> = {
  exploding_bomb: "01", flame: "02", flame_over_circle: "03", gas_cylinder: "04",
  corrosion: "05", skull_and_crossbones: "06", exclamation_mark: "07", health_hazard: "08", environment: "09",
};

/** Display/matching normalization only: callers must retain the original Amazon enum value. */
export function safetyCanonicalCode(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const text = value.trim();
  const ghs = text.match(/^GHS[\s_-]*0?([1-9])$/i)?.[1];
  const alias = text.toLowerCase().replace(/[\s-]+/g, "_");
  const graphic = ghs ? `0${ghs}` : Object.hasOwn(GHS_GRAPHICS, alias) ? GHS_GRAPHICS[alias] : undefined;
  if (graphic) return `GHS${graphic}`;
  const tokens = text.replace(/\b(EUH|H|P)[\s_-]+(?=\d{3})/gi, "$1")
    .split(/\s*(?:\+|_)\s*|\s+(?=(?:EUH|H|P)\d{3})/i);
  const codes: string[] = [];
  for (const token of tokens) {
    const match = token.trim().match(/^(EUH|H|P)(\d{3})([a-z]{0,2})$/i);
    if (!match) return undefined;
    const [, prefix, digits, suffix] = match;
    const family = prefix.toUpperCase();
    let normalizedSuffix = suffix.toUpperCase();
    if (suffix) {
      if (family === "P" || (family === "EUH" && normalizedSuffix !== "A")) return undefined;
      if (family === "H") {
        if (digits === "350" && normalizedSuffix === "I") normalizedSuffix = "I";
        // Upper/lower case differentiates confirmed and suspected effects in H360Fd/H360Df.
        else if (digits === "360" && ["F", "D", "FD", "Fd", "Df"].includes(suffix)) normalizedSuffix = suffix;
        else if (digits === "361" && ["F", "D", "FD"].includes(normalizedSuffix)) normalizedSuffix = suffix.toLowerCase();
        else return undefined;
      }
    }
    if (codes.length && !codes[0].startsWith(family)) return undefined;
    codes.push(`${family}${digits}${normalizedSuffix}`);
  }
  return codes.length ? codes.join("+") : undefined;
}

export function safetyGhsGraphic(value: SafetyPrimitive): string | undefined {
  const code = safetyCanonicalCode(value);
  // Statement codes and ingredient classifications never imply a product pictogram.
  return code?.startsWith("GHS") ? `/ghs/${code}.svg` : undefined;
}

const SAFETY_CODE_LABELS: Record<string, string> = {
  GHS01: "Explodierende Bombe", GHS02: "Flamme", GHS03: "Flamme über einem Kreis",
  GHS04: "Gasflasche", GHS05: "Ätzwirkung", GHS06: "Totenkopf mit gekreuzten Knochen",
  GHS07: "Ausrufezeichen", GHS08: "Gesundheitsgefahr", GHS09: "Umwelt",
  // Source: supplier SDS ELINA Winterapfel, 19.02.2019, section 2.2. P501 remains generic.
  H317: "Kann allergische Hautreaktionen verursachen.",
  H412: "Schädlich für Wasserorganismen, mit langfristiger Wirkung.",
  P101: "Ist ärztlicher Rat erforderlich, Verpackung oder Kennzeichnungsetikett bereithalten.",
  P102: "Darf nicht in die Hände von Kindern gelangen.",
  "P302+P352": "BEI BERÜHRUNG MIT DER HAUT: Mit viel Wasser waschen.",
  "P333+P313": "Bei Hautreizung oder -ausschlag: Ärztlichen Rat einholen/ärztliche Hilfe hinzuziehen.",
  P501: "Inhalt/Behälter der Entsorgung zuführen; Vorgaben im Sicherheitsdatenblatt beachten.",
  // BAuA, Gefahrenhinweise (Februar 2025); EUR-Lex, decision 2017/1219.
  EUH201A: "Achtung! Enthält Blei.",
  EUH209A: "Kann bei Verwendung entzündbar werden.",
  H350I: "Kann bei Einatmen Krebs erzeugen.",
};

// PubChem GHS rev. 11 table, retrieved 2026-10-12; see public/ghs/README.md.
// Only complete H codes whose rows all specify the same single pictogram are illustrated.
// This is a statement illustration, never a selected product label or an Amazon submission value.
const H_STATEMENT_GRAPHICS: Record<string, string> = Object.fromEntries([
  ["01", "H200 H201 H202 H203 H209 H210 H211 H240"],
  ["02", "H205 H206 H207 H208 H220 H222 H223 H224 H225 H226 H228 H230 H231 H232 H242 H250 H251 H252 H260 H261"],
  ["03", "H270 H271 H272"],
  ["04", "H280 H281 H284"],
  ["05", "H290 H314 H318"],
  ["06", "H300 H301 H310 H311 H330 H331 H300+H310 H300+H330 H310+H330 H300+H310+H330 H301+H311 H301+H331 H311+H331 H301+H311+H331"],
  ["07", "H302 H312 H315 H317 H319 H332 H335 H336 H420 H421 H302+H312 H302+H332 H312+H332 H302+H312+H332 H315+H320"],
  ["08", "H304 H305 H334 H340 H341 H350 H350I H351 H360 H360F H360D H360FD H360Fd H360Df H361 H361f H361d H361fd H370 H371 H372 H373"],
  ["09", "H400 H410 H411"],
].flatMap(([graphic, codes]) => codes.split(" ").map(code => [code, `/ghs/GHS${graphic}.svg`])));

function leadingSafetyCode(label: string): { code: string; rest: string } | undefined {
  const text = label.trim();
  const prefix = text.match(/^((?:GHS[\s_-]*0?[1-9]|(?:EUH|H|P)[\s_-]?\d{3}[a-z]{0,2}(?:\s*(?:\+|_|\s+)\s*(?:EUH|H|P)[\s_-]?\d{3}[a-z]{0,2})*))(?=$|[\s:–—-])/i)?.[1];
  const code = safetyCanonicalCode(prefix);
  return code && prefix ? { code, rest: text.slice(prefix.length).replace(/^[\s:–—-]+/, "") } : undefined;
}

/** Amazon descriptions win; the fallback describes a code without supplying compliance answers. */
export function safetyOptionPresentation(value: SafetyPrimitive, label: string): { code?: string; label: string; graphic?: string } {
  const valueCode = safetyCanonicalCode(value);
  const labelCode = leadingSafetyCode(label);
  const code = valueCode ?? labelCode?.code;
  let description = label.trim();
  if (code && safetyCanonicalCode(description) === code) description = "";
  else if (code && labelCode?.code === code) description = labelCode.rest;
  if (!description && code) description = SAFETY_CODE_LABELS[code] ?? "";
  const graphic = safetyGhsGraphic(value) ?? (code ? safetyGhsGraphic(code) ?? H_STATEMENT_GRAPHICS[code] : undefined);
  return { ...(code ? { code } : {}), label: description, ...(graphic ? { graphic } : {}) };
}

const GERMAN_LABELS: Record<string, string> = {
  value: "Wert", manufacturer: "Hersteller", manufacturer_name: "Name des Herstellers",
  manufacturer_contact: "Kontakt des Herstellers", manufacturer_email: "E-Mail des Herstellers",
  responsible_person: "Verantwortliche Person in der EU", eu_responsible_person: "Verantwortliche Person in der EU",
  responsible_person_name: "Name der verantwortlichen Person", contact: "Kontaktangaben", name: "Name",
  email: "E-Mail-Adresse", email_address: "E-Mail-Adresse", contact_email: "E-Mail-Adresse",
  phone: "Telefonnummer", phone_number: "Telefonnummer", address: "Postanschrift", postal_address: "Postanschrift",
  country: "Land", country_code: "Ländercode", postal_code: "Postleitzahl", zip_code: "Postleitzahl", city: "Ort",
  street: "Straße und Hausnummer", street_address: "Straße und Hausnummer", address_line_1: "Straße und Hausnummer",
  address_line_2: "Adresszusatz", state: "Region / Bundesland", url: "Webadresse", contact_url: "Kontakt-Webadresse",
  marketplace_id: "Amazon-Marktplatz", language_tag: "Sprache der Angabe",
};
export function safetyLabel(name: string, schema: SafetySchema, path: string): string {
  if (/gpsr_safety_attestation/i.test(path) && (name === "value" || name === "gpsr_safety_attestation")) return "Ist dieses Produkt von der GPSR-Sicherheitsattestierung ausgenommen?";
  if (/gpsr/i.test(path) && /(?:is_)?exempt(?:ion)?$/i.test(name)) return "Ist dieses Produkt von der GPSR ausgenommen?";
  return GERMAN_LABELS[name] ?? (typeof schema.title === "string" ? schema.title : name.replaceAll("_", " "));
}

export function safetyDisplayValue(value: unknown): string {
  if (value === undefined || value === null || value === "") return "Nicht gesetzt";
  if (typeof value === "boolean") return value ? "Ja" : "Nein";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return JSON.stringify(value, null, 2);
}
